import { useMemo } from "react";

import {
  HEARTBEAT_LANES,
  HEARTBEAT_REPORT_FILENAME,
  HEARTBEAT_TARGETS,
  isHeartbeatReportReady,
  isHeartbeatTerminal,
  type HeartbeatFailureCluster,
  type HeartbeatLane,
  type HeartbeatTarget,
  type HeartbeatTargetSummary,
} from "./airshowHeartbeatContract";
import { useAirshowHeartbeat } from "./useAirshowHeartbeat";
import "./airshow-heartbeat.css";

interface HeartbeatCatalogView {
  catalogRevision: string;
  archiveSha256: string;
  memberCount: 353;
  vspGeometryCount: 352;
  invalidX3dCount: 1;
}
interface AirshowHeartbeatPanelProps {
  active: boolean;
  projectId: string;
  catalog: HeartbeatCatalogView | null;
  onInspectModel: (entryId: string) => void;
}

const TARGET_LABELS: Record<HeartbeatTarget, string> = {
  typed: "Typed",
  "semantic-complete": "Semantic-complete",
  renderable: "Renderable",
  "exact-attested": "Exact-attested",
};
const LANE_ORDER = new Map<HeartbeatLane, number>(
  HEARTBEAT_LANES.map((lane, index) => [lane, index]),
);

function numberLabel(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function timeLabel(value: string | null): string {
  if (!value) return "No pulse yet";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleTimeString() : "Invalid timestamp rejected";
}

function stateCopy(state: string): string {
  if (state === "completed") return "Finite pass completed; review each target independently.";
  if (state === "cancelled") return "Run cancelled; unprocessed aircraft were not evaluated.";
  if (state === "failed") return "Runner failed; no success is inferred for unprocessed aircraft.";
  if (state === "interrupted") return "Runner interrupted; rerun the recorded failures before trusting coverage.";
  if (state === "cancelling") return "Cancellation requested; waiting for the current bounded model to stop.";
  if (state === "queued") return "Queued locally; no aircraft has passed yet.";
  return "Running the pinned 353-member Airshow corpus sequentially.";
}

function targetRows(
  total: number,
  summary: readonly HeartbeatTargetSummary[] | undefined,
): readonly HeartbeatTargetSummary[] {
  return HEARTBEAT_TARGETS.map((target) => summary?.find((item) => item.target === target) ?? {
    target, passed: 0, failed: 0, not_processed: total,
  });
}

function sortedClusters(clusters: readonly HeartbeatFailureCluster[]): readonly HeartbeatFailureCluster[] {
  return [...clusters].sort((left, right) =>
    (LANE_ORDER.get(left.lane) ?? 99) - (LANE_ORDER.get(right.lane) ?? 99)
    || left.code.localeCompare(right.code)
    || left.cluster_id.localeCompare(right.cluster_id));
}

export function AirshowHeartbeatPanel({
  active, projectId, catalog, onInspectModel,
}: AirshowHeartbeatPanelProps) {
  const heartbeat = useAirshowHeartbeat({
    active: active && catalog !== null,
    projectId,
    catalogRevision: catalog?.catalogRevision ?? "unavailable",
    archiveSha256: catalog?.archiveSha256 ?? "unavailable",
  });
  const status = heartbeat.status;
  const terminal = status ? isHeartbeatTerminal(status.state) : false;
  const runOwned = Boolean(status && (!terminal || !isHeartbeatReportReady(status)));
  const targets = targetRows(
    status?.total_models ?? catalog?.memberCount ?? 353,
    heartbeat.report?.summary.targets,
  );
  const clusters = useMemo(
    () => sortedClusters(heartbeat.report?.clusters ?? []),
    [heartbeat.report?.clusters],
  );
  const canRerun = Boolean(status && isHeartbeatReportReady(status) && (status.failed_models > 0 || status.state === "interrupted"));
  const controlsBusy = heartbeat.busy !== null;

  return (
    <section
      aria-labelledby="openvsp-heartbeat-title"
      className="airshow-heartbeat"
      data-state={heartbeat.phase}
      data-testid="openvsp-heartbeat-panel"
      hidden={!active}
    >
      <header className="airshow-heartbeat-hero">
        <div>
          <span>Fleet heartbeat · pinned local corpus</span>
          <h2 id="openvsp-heartbeat-title">Find parser and primitive gaps across Airshow</h2>
          <p data-testid="openvsp-heartbeat-corpus">
            <strong>{catalog?.memberCount ?? 353} total</strong>
            <strong>{catalog?.vspGeometryCount ?? 352} VSP3 aircraft</strong>
            <strong>{catalog?.invalidX3dCount ?? 1} typed X3D negative</strong>
          </p>
        </div>
        <div className="airshow-heartbeat-actions">
          <button data-testid="openvsp-heartbeat-start" disabled={!catalog || runOwned || controlsBusy} onClick={() => void heartbeat.startFull()} type="button">
            {heartbeat.busy === "start" ? "Starting…" : "Start full pass"}
          </button>
          <button data-testid="openvsp-heartbeat-cancel" disabled={!status || terminal || controlsBusy} onClick={() => void heartbeat.cancel()} type="button">
            {heartbeat.busy === "cancel" ? "Cancelling…" : "Cancel"}
          </button>
          <button data-testid="openvsp-heartbeat-rerun" disabled={!canRerun || controlsBusy} onClick={() => void heartbeat.rerunFailures()} type="button">
            Rerun failures
          </button>
        </div>
        <p data-testid="openvsp-heartbeat-honesty">
          Local diagnostic only. It reads the pinned archive and writes a report; it does not change parser code,
          source aircraft, or geometry. Remedy hints are proposed, never automatic. A pass is not a fidelity certification.
        </p>
      </header>

      {heartbeat.phase === "loading" && !status ? <p className="airshow-heartbeat-notice" data-testid="openvsp-heartbeat-loading">Loading local heartbeat state…</p> : null}
      {heartbeat.message ? <p className="airshow-heartbeat-error" data-testid="openvsp-heartbeat-error" role="alert">{heartbeat.message}</p> : null}
      {heartbeat.pollMessage ? <p className="airshow-heartbeat-notice" data-testid="openvsp-heartbeat-poll-message">{heartbeat.pollMessage}</p> : null}
      {!catalog ? <p className="airshow-heartbeat-error" role="alert">The verified 353-member Airshow catalog is unavailable; heartbeat controls stay disabled.</p> : null}

      <div className="airshow-heartbeat-grid">
        <section className="airshow-heartbeat-run">
          <header>
            <div><span>Run state</span><strong data-state={status?.state ?? "idle"} data-testid="openvsp-heartbeat-state">{status?.state ?? "idle"}</strong></div>
            <p>{status ? stateCopy(status.state) : "No local run is active. Start a finite 353-member corpus pass: 352 VSP geometries + 1 typed X3D negative."}</p>
          </header>
          <div className="airshow-heartbeat-progress">
            <div>
              <span>Processed</span>
              <strong data-testid="openvsp-heartbeat-progress">{numberLabel(status?.processed_models ?? 0)} / {numberLabel(status?.total_models ?? 353)}</strong>
            </div>
            <progress max={status?.total_models || 353} value={status?.processed_models ?? 0} />
            <dl>
              <div><dt>Succeeded models</dt><dd>{numberLabel(status?.succeeded_models ?? 0)}</dd></div>
              <div><dt>Failed models</dt><dd>{numberLabel(status?.failed_models ?? 0)}</dd></div>
              <div><dt>Event revision</dt><dd>{status?.event_sequence ?? 0}</dd></div>
            </dl>
          </div>
          <div className="airshow-heartbeat-current">
            <span>Current aircraft</span>
            <code data-testid="openvsp-heartbeat-current-model">{status?.current_model?.entry_id ?? (terminal ? "No model active" : "Waiting for runner")}</code>
            <span>Last pulse · {timeLabel(status?.last_pulse_at ?? null)}</span>
            {status?.pulse_stale ? <strong data-testid="openvsp-heartbeat-stale">Pulse stale — displayed progress is not current.</strong> : null}
          </div>
          {status?.error ? <p className="airshow-heartbeat-error" role="alert"><strong>{status.error.code}</strong> · {status.error.message}{status.error.retryable ? " Retry is permitted." : " Retry is not classified safe."}</p> : null}
          {status?.report_revision ? <div className="airshow-heartbeat-report" data-testid="openvsp-heartbeat-report-file">
            <span>Codex report</span><code>{HEARTBEAT_REPORT_FILENAME}</code>
            <span>SHA-256 revision</span><code>{status.report_revision}</code>
          </div> : null}
        </section>

        <section className="airshow-heartbeat-targets">
          <header><span>Independent coverage targets</span><p>Typed source, complete semantic reconstruction, complete realized display, and exact attestation are counted separately. These are evidence outcomes, not geometric equivalence scores.</p></header>
          {targets.map((target) => {
            const total = target.passed + target.failed + target.not_processed;
            return <article data-testid="openvsp-heartbeat-target" key={target.target}>
              <div><strong>{TARGET_LABELS[target.target]}</strong><span>{numberLabel(target.passed)} passed · {numberLabel(target.failed)} failed · {numberLabel(target.not_processed)} not processed</span></div>
              <progress aria-label={`${TARGET_LABELS[target.target]} passed`} max={total || 1} value={target.passed} />
            </article>;
          })}
        </section>
      </div>

      <div className="airshow-heartbeat-evidence">
        <section className="airshow-heartbeat-clusters">
          <header><span>Failure clusters</span><strong>{clusters.length}</strong></header>
          {clusters.length === 0 ? <p>{terminal ? "No clustered failures were recorded." : "Clusters appear only after the terminal report is written."}</p> : clusters.map((cluster) =>
            <details data-testid="openvsp-heartbeat-cluster" key={cluster.cluster_id}>
              <summary><span>{cluster.lane}</span><strong>{cluster.code}</strong><i>{numberLabel(cluster.count)} models</i></summary>
              <div>
                <p>{cluster.feature_declarations.length ? `Declarations · ${cluster.feature_declarations.join(", ")}` : "No feature declaration was attached."}</p>
                <p><strong>Remedy proposal · {cluster.remedy.kind}</strong><br />{cluster.remedy.summary}</p>
                <p className="airshow-heartbeat-manual">Automatic false · manual implementation and verification required.</p>
                <ul>{cluster.model_entry_ids.map((entryId) => <li key={entryId}><code>{entryId}</code><button onClick={() => onInspectModel(entryId)} type="button">Inspect in Compare</button></li>)}</ul>
              </div>
            </details>)}
        </section>

        <section className="airshow-heartbeat-models">
          <header><span>Model evidence · six lanes</span><strong>{heartbeat.page ? `${heartbeat.page.cursor + 1}–${heartbeat.page.cursor + heartbeat.page.items.length} of ${heartbeat.page.total}` : "Terminal page pending"}</strong></header>
          {heartbeat.page?.items.map((model) => <article data-testid="openvsp-heartbeat-model" key={model.entry_id}>
            <div><code>{model.entry_id}</code><span>{numberLabel(model.duration_ms)} ms</span><button data-testid="openvsp-heartbeat-inspect-model" onClick={() => onInspectModel(model.entry_id)} type="button">Inspect in Compare</button></div>
            <div className="airshow-heartbeat-lanes">{model.lanes.map((lane) => <div data-status={lane.status} data-testid="openvsp-heartbeat-lane" key={lane.lane}><span>{lane.lane}</span><strong>{lane.status}</strong><code>{lane.code}</code></div>)}</div>
          </article>)}
          {!heartbeat.page ? <p>{terminal ? "Loading the first bounded evidence page…" : "Model lane evidence is paged after the run reaches a terminal state."}</p> : null}
          {heartbeat.page ? <footer>
            <button disabled={heartbeat.page.cursor === 0 || heartbeat.busy === "page"} onClick={() => void heartbeat.loadPage(Math.max(0, heartbeat.page!.cursor - heartbeat.page!.limit))} type="button">Previous page</button>
            <button disabled={heartbeat.page.next_cursor === null || heartbeat.busy === "page"} onClick={() => void heartbeat.loadPage(heartbeat.page!.next_cursor!)} type="button">Next page</button>
          </footer> : null}
        </section>
      </div>
    </section>
  );
}
